import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getAllFinancementsSA, updateFinancementSA, deleteFinancementSA, getFinancementDetailsSA, companiesForAdmins } from '../../../api/redux/slices/SuperAdmin';
import { LoaderComponent } from '../../../helpers/components/loader';
import { convertDate, roundToThreeDecimals } from '../../../helpers/modules';
import { icons } from '../../../assets/icons';
import { toast } from 'react-toastify';
import './index.scss';

const FinancementSuperAdmin = () => {
    const dispatch = useDispatch();
    const { financements, loading, total, financementStats, allCompanies } = useSelector((state) => state.superAdmin);
    const token = localStorage.getItem('accesstoken');
    
    const [page, setPage] = useState(1);
    const [statusFilter, setStatusFilter] = useState('');
    const [companyFilter, setCompanyFilter] = useState('');
    const [selectedFinancement, setSelectedFinancement] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [formData, setFormData] = useState({
        sujet: '',
        description: '',
        price: '',
        status: '',
        message: ''
    });
    const [formErrors, setFormErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const limit = 9;

    const fetchFinancements = useCallback(async () => {
        try {
            await dispatch(getAllFinancementsSA({ 
                token, 
                page,
                status: statusFilter || undefined,
                company: companyFilter || undefined
            })).unwrap();
        } catch (error) {
            console.error('Error fetching financements:', error);
        }
    }, [dispatch, token, page, statusFilter, companyFilter]);

    useEffect(() => {
        dispatch(companiesForAdmins({ token }));
        fetchFinancements();
    }, [fetchFinancements, dispatch, token]);

    const handlePageChange = (newPage) => {
        setPage(newPage);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleStatusFilter = (status) => {
        setStatusFilter(status);
        setPage(1);
    };

    const handleCompanyFilter = (companyId) => {
        setCompanyFilter(companyId);
        setPage(1);
    };

    const handleShowDetails = async (financement) => {
        try {
            const result = await dispatch(getFinancementDetailsSA({ token, id: financement._id })).unwrap();
            setSelectedFinancement(result.data);
            setShowModal(true);
        } catch (error) {
            toast.error('Erreur lors du chargement des détails');
        }
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setSelectedFinancement(null);
    };

    const handleOpenEditModal = (financement) => {
        setSelectedFinancement(financement);
        setFormData({
            sujet: financement.sujet || '',
            description: financement.description || '',
            price: financement.price || '',
            status: financement.status || 'en cours',
            message: financement.message || ''
        });
        setFormErrors({});
        setShowEditModal(true);
    };

    const handleCloseEditModal = () => {
        setShowEditModal(false);
        setSelectedFinancement(null);
        setFormData({ sujet: '', description: '', price: '', status: '', message: '' });
        setFormErrors({});
    };

    const handleOpenDeleteModal = (financement) => {
        setSelectedFinancement(financement);
        setShowDeleteModal(true);
    };

    const handleCloseDeleteModal = () => {
        setShowDeleteModal(false);
        setSelectedFinancement(null);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (formErrors[name]) {
            setFormErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const validateForm = () => {
        const errors = {};
        if (!formData.sujet.trim()) errors.sujet = 'Le sujet est requis';
        if (!formData.description.trim()) errors.description = 'La description est requise';
        if (!formData.price || parseFloat(formData.price) <= 0) errors.price = 'Le montant doit être supérieur à 0';
        if (!formData.status) errors.status = 'Le statut est requis';
        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleUpdate = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;
        setSubmitting(true);
        try {
            await dispatch(updateFinancementSA({
                token,
                id: selectedFinancement._id,
                data: {
                    sujet: formData.sujet,
                    description: formData.description,
                    price: parseFloat(formData.price),
                    status: formData.status,
                    message: formData.message
                }
            })).unwrap();
            toast.success('Financement mis à jour avec succès');
            handleCloseEditModal();
            fetchFinancements();
        } catch (error) {
            toast.error(error?.message || 'Erreur lors de la mise à jour');
        } finally {
            setSubmitting(false);
        }
    };

    const handleDelete = async () => {
        setSubmitting(true);
        try {
            await dispatch(deleteFinancementSA({ token, id: selectedFinancement._id })).unwrap();
            toast.success('Financement supprimé avec succès');
            handleCloseDeleteModal();
            fetchFinancements();
        } catch (error) {
            toast.error(error?.message || 'Erreur lors de la suppression');
        } finally {
            setSubmitting(false);
        }
    };

    const truncateText = (text, maxLength) => {
        if (!text) return '-';
        return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
    };

    const getStatusBadge = (status) => {
        switch(status) {
            case 'accepted': return { className: 'status-accepted', label: 'Accepté', icon: 'BsCheckCircle' };
            case 'refused': return { className: 'status-refused', label: 'Refusé', icon: 'BsXCircle' };
            default: return { className: 'status-pending', label: 'En cours', icon: 'BsClock' };
        }
    };

    const totalPages = Math.ceil(total / limit);

    return (
        <div id="financementSA">
            <div className="financement-header">
                <div className="header-content">
                    <div className="header-icon">
                        <icons.BsCashStack style={{ fontSize: '24px' }} />
                    </div>
                    <div className="header-text">
                        <h1>Gestion des Financements</h1>
                        <p>Gérez toutes les demandes de financement des sociétés</p>
                    </div>
                </div>
            </div>

            {/* Stats Section */}
            {financementStats && (
                <div className="stats-cards">
                    <div className="stat-item pending">
                        <icons.BsClock style={{ fontSize: '20px' }} />
                        <div className="stat-info">
                            <span className="stat-num">{financementStats.pendingCount}</span>
                            <span className="stat-lbl">En cours</span>
                        </div>
                    </div>
                    <div className="stat-item accepted">
                        <icons.BsCheckCircle style={{ fontSize: '20px' }} />
                        <div className="stat-info">
                            <span className="stat-num">{financementStats.acceptedCount}</span>
                            <span className="stat-lbl">Acceptés</span>
                        </div>
                    </div>
                    <div className="stat-item refused">
                        <icons.BsXCircle style={{ fontSize: '20px' }} />
                        <div className="stat-info">
                            <span className="stat-num">{financementStats.refusedCount}</span>
                            <span className="stat-lbl">Refusés</span>
                        </div>
                    </div>
                    <div className="stat-item total">
                        <icons.BsGraphUp style={{ fontSize: '20px' }} />
                        <div className="stat-info">
                            <span className="stat-num">{roundToThreeDecimals(financementStats.totalFinancements)} DT</span>
                            <span className="stat-lbl">Total Financements</span>
                        </div>
                    </div>
                </div>
            )}

            {/* Filters Row */}
            <div className="filters-row">
                <div className="status-filter">
                    <button className={`filter-btn ${statusFilter === '' ? 'active' : ''}`} onClick={() => handleStatusFilter('')}>Tous</button>
                    <button className={`filter-btn pending ${statusFilter === 'en cours' ? 'active' : ''}`} onClick={() => handleStatusFilter('en cours')}>
                        <icons.BsClock style={{ fontSize: '14px' }} />En cours
                    </button>
                    <button className={`filter-btn accepted ${statusFilter === 'accepted' ? 'active' : ''}`} onClick={() => handleStatusFilter('accepted')}>
                        <icons.BsCheckCircle style={{ fontSize: '14px' }} />Accepté
                    </button>
                    <button className={`filter-btn refused ${statusFilter === 'refused' ? 'active' : ''}`} onClick={() => handleStatusFilter('refused')}>
                        <icons.BsXCircle style={{ fontSize: '14px' }} />Refusé
                    </button>
                </div>

                <div className="company-filter">
                    <select 
                        value={companyFilter} 
                        onChange={(e) => handleCompanyFilter(e.target.value)}
                        className="company-select"
                    >
                        <option value="">Toutes les sociétés</option>
                        {allCompanies?.map(company => (
                            <option key={company._id} value={company._id}>
                                {company.name}
                            </option>
                        ))}
                    </select>
                </div>
            </div>

            {loading ? (
                <LoaderComponent />
            ) : (
                <>
                    <div className="financement-grid">
                        {financements?.map((financement, index) => {
                            const statusBadge = getStatusBadge(financement.status);
                            const StatusIcon = icons[statusBadge.icon];
                            
                            return (
                                <div className="financement-card" key={financement._id || index}>
                                    <div className={`card-status-bar ${statusBadge.className}`}></div>
                                    <div className="card-content">
                                        <div className="card-header">
                                            <div className="sujet">
                                                <icons.BsFileText className="sujet-icon" />
                                                <span>{truncateText(financement.sujet, 50)}</span>
                                            </div>
                                            <div className={`status-badge ${statusBadge.className}`}>
                                                <StatusIcon style={{ fontSize: '14px' }} />
                                                <span>{statusBadge.label}</span>
                                            </div>
                                        </div>
                                        <div className="card-body">
                                            <div className="company-name-row">
                                                <icons.BsBuilding style={{ fontSize: '14px', color: '#35435f' }} />
                                                <span>{financement.company?.name || '-'}</span>
                                            </div>
                                            <p className="description">{truncateText(financement.description, 40)}</p>
                                            {financement.date_limite && (
                                                <div className="date-limite-row">
                                                    <span>📅</span>
                                                    <span>Date limite : {convertDate(financement.date_limite, 'DD/MM/YYYY')}</span>
                                                </div>
                                            )}
                                        </div>
                                        <div className="card-footer">
                                            <div className="price-section">
                                                <span className="price-label">Montant</span>
                                                <span className="price-value">{roundToThreeDecimals(financement.price)} DT</span>
                                            </div>
                                            <div className="actions-section">
                                                <span className="date">{convertDate(financement.createdAt, 'DD/MM/YYYY')}</span>
                                                <div className="action-btns">
                                                    <button className="btn-icon edit" onClick={() => handleOpenEditModal(financement)} title="Modifier">
                                                        <icons.BsPencil style={{ fontSize: '14px' }} />
                                                    </button>
                                                    <button className="btn-icon delete" onClick={() => handleOpenDeleteModal(financement)} title="Supprimer">
                                                        <icons.BsTrash style={{ fontSize: '14px' }} />
                                                    </button>
                                                </div>
                                                <button className="btn-details" onClick={() => handleShowDetails(financement)} title="Voir détails">
                                                    <icons.BsEye style={{ fontSize: '16px' }} />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {financements?.length === 0 && !loading && (
                        <div className="empty-state">
                            <icons.BsInbox style={{ fontSize: '48px', color: '#94a3b8' }} />
                            <h3>Aucun financement trouvé</h3>
                            <p>Aucune demande de financement n'a été trouvée.</p>
                        </div>
                    )}

                    {totalPages > 1 && (
                        <div className="pagination">
                            <button className="page-btn" onClick={() => handlePageChange(page - 1)} disabled={page === 1}>
                                <icons.BsChevronLeft />
                            </button>
                            {[...Array(totalPages)].map((_, index) => (
                                <button key={index} className={`page-btn ${page === index + 1 ? 'active' : ''}`} onClick={() => handlePageChange(index + 1)}>
                                    {index + 1}
                                </button>
                            ))}
                            <button className="page-btn" onClick={() => handlePageChange(page + 1)} disabled={page === totalPages}>
                                <icons.BsChevronRight />
                            </button>
                        </div>
                    )}
                </>
            )}

            {/* Details Modal */}
            {showModal && selectedFinancement && (
                <div className="modal-overlay" onClick={handleCloseModal}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>Détails du financement</h2>
                            <button className="modal-close" onClick={handleCloseModal}><icons.BsX style={{ fontSize: '24px' }} /></button>
                        </div>
                        <div className="modal-body">
                            <div className={`modal-status-bar ${getStatusBadge(selectedFinancement.status).className}`}>
                                {(() => {
                                    const badge = getStatusBadge(selectedFinancement.status);
                                    const StatusIcon = icons[badge.icon];
                                    return <><StatusIcon style={{ fontSize: '20px' }} /><span>{badge.label}</span></>;
                                })()}
                            </div>

                            <div className="detail-row">
                                <label>Société</label>
                                <p className="company-name-detail">
                                    <icons.BsBuilding style={{ marginRight: '8px' }} />
                                    {selectedFinancement.company?.name || '-'}
                                </p>
                            </div>

                            {/* Company Solde */}
                            {selectedFinancement.companySolde && (
                                <div className="company-solde-card">
                                    <h3>Solde de la société</h3>
                                    <div className="solde-lines">
                                        <div className="solde-line">
                                            <span>Parrainage reçu</span>
                                            <span className="positive">+ {roundToThreeDecimals(selectedFinancement.companySolde.totalParrainageReceived)} DT</span>
                                        </div>
                                        <div className="solde-line">
                                            <span>Financements acceptés</span>
                                            <span className="negative">- {roundToThreeDecimals(selectedFinancement.companySolde.totalAcceptedFinancements)} DT</span>
                                        </div>
                                        <div className="solde-line total">
                                            <span>Solde disponible</span>
                                            <span className={selectedFinancement.companySolde.solde >= 0 ? 'positive' : 'negative'}>
                                                {selectedFinancement.companySolde.solde >= 0 ? '+ ' : '- '}
                                                {roundToThreeDecimals(Math.abs(selectedFinancement.companySolde.solde))} DT
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            )}

                            <div className="detail-row"><label>Sujet</label><p>{selectedFinancement.sujet || '-'}</p></div>
                            <div className="detail-row"><label>Description</label><p className="description-full">{selectedFinancement.description || '-'}</p></div>
                            <div className="detail-row"><label>Montant demandé</label><p className="price-highlight">{roundToThreeDecimals(selectedFinancement.price)} DT</p></div>
                            {selectedFinancement.date_limite && (
                                <div className="detail-row"> 
                                    <label>Date limite</label>
                                    <p className="date-limite-highlight">
                                        <span style={{ marginRight: '8px' }}>📅</span>
                                        {convertDate(selectedFinancement.date_limite, 'DD/MM/YYYY')}
                                    </p>
                                </div>
                            )}
                            <div className="detail-row"><label>Date de demande</label><p>{convertDate(selectedFinancement.createdAt, 'DD/MM/YYYY à HH:mm')}</p></div>
                            
                            {selectedFinancement.devise_proof?.secure_url && (
                                <div className="detail-row">
                                    <label>Justificatif (Devis)</label>
                                    <div className="devise-proof-preview">
                                        <img 
                                            src={selectedFinancement.devise_proof.secure_url} 
                                            alt="Justificatif devis" 
                                            className="devise-proof-img"
                                            onClick={() => window.open(selectedFinancement.devise_proof.secure_url, '_blank')}
                                        />
                                        <a 
                                            href={selectedFinancement.devise_proof.secure_url} 
                                            target="_blank" 
                                            rel="noreferrer"
                                            className="devise-proof-link"
                                        >
                                            <icons.BsEye style={{ fontSize: '16px' }} />
                                            Voir le justificatif en plein écran
                                        </a>
                                    </div>
                                </div>
                            )}

                            {selectedFinancement.message && (
                                <div className={`admin-message ${selectedFinancement.status === 'accepted' ? 'message-accepted' : 'message-refused'}`}>
                                    <div className="message-header">
                                        {selectedFinancement.status === 'accepted' ? <icons.BsCheckCircle style={{ fontSize: '18px' }} /> : <icons.BsXCircle style={{ fontSize: '18px' }} />}
                                        <span>Message</span>
                                    </div>
                                    <p className="message-text">{selectedFinancement.message}</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Edit Modal */}
            {showEditModal && selectedFinancement && (
                <div className="modal-overlay" onClick={handleCloseEditModal}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>Modifier le financement</h2>
                            <button className="modal-close" onClick={handleCloseEditModal}><icons.BsX style={{ fontSize: '24px' }} /></button>
                        </div>
                        <form onSubmit={handleUpdate}>
                            <div className="modal-body">
                                <div className="form-group">
                                    <label>Société</label>
                                    <p className="company-display"><icons.BsBuilding style={{ marginRight: '6px' }} />{selectedFinancement.company?.name || '-'}</p>
                                </div>
                                <div className="form-group">
                                    <label htmlFor="sujet">Sujet <span className="required">*</span></label>
                                    <input type="text" id="sujet" name="sujet" value={formData.sujet} onChange={handleInputChange} className={`form-input ${formErrors.sujet ? 'input-error' : ''}`} />
                                    {formErrors.sujet && <span className="error-text">{formErrors.sujet}</span>}
                                </div>
                                <div className="form-group">
                                    <label htmlFor="description">Description <span className="required">*</span></label>
                                    <textarea id="description" name="description" value={formData.description} onChange={handleInputChange} rows="3" className={`form-input form-textarea ${formErrors.description ? 'input-error' : ''}`} />
                                    {formErrors.description && <span className="error-text">{formErrors.description}</span>}
                                </div>
                                <div className="form-row">
                                    <div className="form-group">
                                        <label htmlFor="price">Montant (DT) <span className="required">*</span></label>
                                        <input type="number" id="price" name="price" value={formData.price} onChange={handleInputChange} step="0.001" min="0" className={`form-input ${formErrors.price ? 'input-error' : ''}`} />
                                        {formErrors.price && <span className="error-text">{formErrors.price}</span>}
                                    </div>
                                    <div className="form-group">
                                        <label htmlFor="status">Statut <span className="required">*</span></label>
                                        <select id="status" name="status" value={formData.status} onChange={handleInputChange} className={`form-input ${formErrors.status ? 'input-error' : ''}`}>
                                            <option value="en cours">En cours</option>
                                            <option value="accepted">Accepté</option>
                                            <option value="refused">Refusé</option>
                                        </select>
                                        {formErrors.status && <span className="error-text">{formErrors.status}</span>}
                                    </div>
                                </div>
                                <div className="form-group">
                                    <label htmlFor="message">Message</label>
                                    <textarea id="message" name="message" value={formData.message} onChange={handleInputChange} rows="2" placeholder="Message optionnel..." className="form-input form-textarea" />
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn-cancel" onClick={handleCloseEditModal} disabled={submitting}>Annuler</button>
                                <button type="submit" className="btn-submit" disabled={submitting}>
                                    {submitting ? <><span className="spinner"></span><span>Modification...</span></> : <><icons.BsPencil style={{ fontSize: '18px' }} /><span>Modifier</span></>}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Delete Modal */}
            {showDeleteModal && selectedFinancement && (
                <div className="modal-overlay" onClick={handleCloseDeleteModal}>
                    <div className="modal-content modal-confirm" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>Confirmer la suppression</h2>
                            <button className="modal-close" onClick={handleCloseDeleteModal}><icons.BsX style={{ fontSize: '24px' }} /></button>
                        </div>
                        <div className="modal-body">
                            <div className="confirm-content">
                                <div className="confirm-icon"><icons.BsExclamationTriangle style={{ fontSize: '48px', color: '#ef4444' }} /></div>
                                <p className="confirm-text">Êtes-vous sûr de vouloir supprimer ce financement ?</p>
                                <div className="confirm-details">
                                    <span className="confirm-sujet">{selectedFinancement.sujet}</span>
                                    <span className="confirm-company">{selectedFinancement.company?.name}</span>
                                    <span className="confirm-price">{roundToThreeDecimals(selectedFinancement.price)} DT</span>
                                </div>
                                <p className="confirm-warning">Cette action est irréversible.</p>
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button type="button" className="btn-cancel" onClick={handleCloseDeleteModal} disabled={submitting}>Annuler</button>
                            <button type="button" className="btn-delete" onClick={handleDelete} disabled={submitting}>
                                {submitting ? <><span className="spinner"></span><span>Suppression...</span></> : <><icons.BsTrash style={{ fontSize: '18px' }} /><span>Supprimer</span></>}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default FinancementSuperAdmin;